/**
 * Lightweight analytics dispatcher.
 *
 * Pushes events to:
 *  - window.gtag (Google Analytics 4), when present
 *  - window.dataLayer (GTM), when present
 *
 * Silently no-ops when no analytics provider is loaded, so it is safe to
 * call from any component without guarding.
 */

type Params = Record<string, string | number | boolean | undefined | null>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export type AnalyticsEvent = {
  name: string;
  params: Record<string, unknown>;
  ts: number;
};

type Listener = (e: AnalyticsEvent) => void;
const listeners = new Set<Listener>();

/**
 * Subscribe to every dispatched analytics event. Used by the in-app
 * dev debug panel and the FAQ funnel tally. Returns an unsubscribe fn.
 */
export function subscribeAnalytics(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Events that should be persisted to the `funnel_events` table for the
 * maternity outfit guide admin dashboard. Keep in sync with the table's
 * INSERT policy allowlist.
 */
const PERSISTED_EVENTS = new Set([
  "maternity_cta_click",
  "checkout_initiated",
  "checkout_completed",
  "checkout_failed",
]);

async function persistFunnelEvent(name: string, params: Record<string, unknown>) {
  try {
    if (!PERSISTED_EVENTS.has(name)) return;
    const campaign = (params.campaign ?? params.utm_campaign) as string | undefined;
    // Only track checkout_* when they belong to a campaign we care about.
    if (name !== "maternity_cta_click" && campaign !== "maternity_outfit_guide") return;
    const { supabase } = await import("@/integrations/supabase/client");
    await (supabase as any).from("funnel_events").insert({
      name,
      campaign: campaign ?? null,
      placement: (params.placement as string) ?? null,
      destination: (params.destination as string) ?? null,
      slot_start: (params.slot_start as string) ?? null,
      studio_id: (params.studio_id as string) ?? null,
      session_id: (params.session_id as string) ?? null,
      params,
    });
  } catch {
    /* analytics is best-effort */
  }
}

export function trackEvent(name: string, params: Params = {}): void {
  if (typeof window === "undefined") return;
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    cleaned[k] = v;
  }
  try {
    window.gtag?.("event", name, cleaned);
  } catch {
    /* ignore */
  }
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...cleaned });
  } catch {
    /* ignore */
  }
  void persistFunnelEvent(name, cleaned);
  const evt: AnalyticsEvent = { name, params: cleaned, ts: Date.now() };
  listeners.forEach((l) => {
    try {
      l(evt);
    } catch {
      /* ignore listener failures */
    }
  });
  if (import.meta.env.DEV) {
     
    console.debug("[analytics]", name, cleaned);
  }
}
